import React from 'react';
import i18next from 'i18next';

// Translated when the notification is shown, not when this module loads: the
// language can change while the app is open.
const t = (key) => i18next.t(`confirmations:patientSync.${key}`);

const renderRows = (rows) => (
  <div>
    {rows.map(({ label, count }) => (
      <div key={label}>
        {label}: {count}
      </div>
    ))}
  </div>
);

/**
 * The notification shown after a sync to Patient Service was scheduled.
 *
 * One row per kind of record rather than a single total: a total adds patients,
 * activities and answer sheets together, which matches nothing the operator can
 * see on the screen.
 *
 * @param {object} result of api.zedoc.forceSyncToPatientService / syncActivity
 * @returns {{ message: string, description?: React.ReactNode }} for notifySuccess
 */
const formatSyncCounts = ({
  nPatients = 0,
  nActivities = 0,
  nAnswersSheets = 0,
  nProjects = 0,
} = {}) => {
  const rows = [
    { label: t('rows.patients'), count: nPatients },
    { label: t('rows.activities'), count: nActivities },
    { label: t('rows.answersSheets'), count: nAnswersSheets },
  ].filter(({ count }) => count > 0);
  if (rows.length === 0) {
    return {
      message: nProjects > 0 ? t('sync.projectOnly') : t('sync.nothing'),
    };
  }
  return {
    message: t('sync.title'),
    description: renderRows(rows),
  };
};

/**
 * The notification shown after a re-sync FROM the Patient App was requested.
 *
 * Both numbers, deliberately. They differ whenever a matched item never reached
 * the Patient App - a notification cancelled before sending, an answer sheet
 * not completed yet - and showing only the smaller one reads as a failure when
 * it is the correct answer.
 *
 * @param {string} namespace the block under confirmations:patientSync holding
 *   `title`, `matched` (everything the cohort matched) and `resynced` (what
 *   the Patient App holds)
 * @returns {(result: { nRecords?: number, nRequested?: number }) => object}
 */
const createFormatResyncCounts =
  (namespace) =>
  ({ nRecords = 0, nRequested = 0 } = {}) => ({
    message: nRecords > 0 ? t(`${namespace}.title`) : t('resync.nothing'),
    description: renderRows([
      { label: t(`${namespace}.matched`), count: nRequested },
      { label: t(`${namespace}.resynced`), count: nRecords },
    ]),
  });

export const formatResyncNotificationsCounts = createFormatResyncCounts(
  'resyncNotifications',
);

export const formatResyncCompletedCounts =
  createFormatResyncCounts('resyncCompleted');

/**
 * The notification shown after a bulk reminder was queued.
 *
 * @param {object} result of api.zedoc.resendNotifications
 */
export const formatResendNotificationsCounts = ({
  nActivities = 0,
  nPatients = 0,
} = {}) => ({
  message: t('resend.title'),
  description: renderRows(
    [
      { label: t('rows.patients'), count: nPatients },
      { label: t('rows.activities'), count: nActivities },
    ].filter(({ count }) => count > 0),
  ),
});

export default formatSyncCounts;
